<script setup lang="ts">
	import { ref } from "vue";
	import { DataTableFilterMetaData } from "primevue/datatable";
	import DataTable from "primevue/datatable";
	import Loading from "@/components/Loading.vue";
	import Search from "@/components/Search.vue";
	import ActionButton from "@/components/ActionButton.vue";
	import Column from "primevue/column";
	import { AuditTrailsType } from "@/composables/api-models";
	import { AuditTrailsPageType } from "@/composables/audit-trails-api";
	import { getDuration } from "@/composables/time";
	import { formatDateTime } from "@ptx-autonomous/libs/formatting";
	import router from "@/router";
	import CopyTextButton from "@/components/CopyTextButton.vue";

	const props = defineProps<{
		tasks: (AuditTrailsType & {
			organizationName?: string;
			organizationId?: string;
		})[];
		type: AuditTrailsPageType; // type of the history, either active or history
		isLoading: boolean;
		errorMessage?: string;
	}>();

	const emit = defineEmits<{
		terminateConnection: [remoteId: string];
	}>();

	// this holds the filter values from primevue table
	const filters = ref<{ global: DataTableFilterMetaData }>({
		global: {
			value: null,
			matchMode: "contains",
		},
	});

	function ExportActionClicked() {
		downloadCSV();
	}

	function convertToCSV() {
		var items = props.tasks;
		var csv = "";

		if (items) {
			const headers = Object.keys(items[0]) as (keyof AuditTrailsType)[];
			csv += headers.join(",") + "\n";

			items.forEach((item: AuditTrailsType) => {
				csv +=
					headers.map(header => replacer(item[header] as string)).join(",") +
					"\n";
			});
		}
		return csv;
	}

	function replacer(value: string) {
		return value === null ? "" : value;
	}

	function downloadCSV() {
		const filenameWithoutExtension = ref("");
		if (props.type === AuditTrailsPageType.Active) {
			filenameWithoutExtension.value = "ActiveECSTasks";
		} else {
			filenameWithoutExtension.value = "HistoryECSTasks";
		}
		const csvData = convertToCSV();
		const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);

		const today = new Date();
		const currtime =
			today.getDate() + "_" + today.getMonth() + "_" + today.getFullYear();

		link.setAttribute("href", url);
		link.setAttribute(
			"download",
			`${filenameWithoutExtension.value}_${currtime}.csv`,
		);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	// Store the selected task in local storage to avoid refetching data
	// when navigating to the details page. This is necessary because
	// the data is split across two relational tables in DynamoDB, and
	// we cannot perform joins directly.
	function storeTask(
		task: AuditTrailsType & {
			organizationId?: string;
			organizationName?: string;
		},
	) {
		localStorage.setItem("task", JSON.stringify(task));
		router.push({ name: "historyDetails" });
	}
</script>

<template>
	<div class="box-border flex min-h-0 flex-col gap-4 p-4">
		<div
			class="customizedPrimeVueTable flex grid grid-cols-4 flex-col gap-2 gap-4 overflow-y-auto"
		>
			<DataTable
				tableStyle="min-width: 100%"
				paginator
				:loading="isLoading"
				:filters="filters"
				:rows="10"
				:value="props.tasks"
				class="col-span-4"
			>
				<template #header>
					<div
						class="mb-5 flex justify-between"
						v-if="props.tasks"
					>
						<Search :filters-prop="filters" />
						<!-- Search component -->
						<div style="display: flex">
							<ActionButton
								:action-text="'Export'"
								@action="ExportActionClicked"
								:img-src="'export_icon'"
							/>
							<!-- <ActionButton
								:action-text="'This Week'"
								@action="CalendarActionClicked"
								:img-src="'calendar_icon'"
								v-if="props.type === AuditTrailsPageType.History"
							/> -->
						</div>
					</div>
				</template>
				<Column
					field="ProjectName"
					header="Project Name"
					sortable
				/>
				<Column
					field="organizationName"
					header="OrganizationName"
					sortable
				/>
				<Column
					field="MachineName"
					header="Machine Name"
					sortable
					v-if="props.type === AuditTrailsPageType.Active"
				/>
				<Column
					field="SerialNumber"
					header="Serial Number"
					sortable
				/>

				<Column
					field="Password"
					header="Password"
					sortable
				>
					<template #body="slotProps">
						<CopyTextButton
							:text="slotProps.data.Password"
							:displayText="slotProps.data.Password"
							v-if="
								slotProps.data.Password && slotProps.data.Password !== 'N/A'
							"
						/>
						<span v-else>N/A</span>
					</template>
				</Column>

				<Column
					field="PublicIP"
					header="Public IP"
					sortable
					style="width: auto"
				>
					<template #body="slotProps">
						<div
							style="
								display: flex;
								align-items: center;
								justify-content: center;
							"
						>
							<CopyTextButton
								:text="`ssh -p 2281 jca@${slotProps.data.PublicIP}`"
								:displayText="slotProps.data.PublicIP"
								v-if="type === AuditTrailsPageType.Active"
							/>
							<CopyTextButton
								:text="`ssh -o ProxyCommand='ssh -W %h:%p -p 2281 -o ServerAliveInterval=60 jca@${slotProps.data.PublicIP}' -o ServerAliveInterval=60 jca@127.0.0.1 -p 4444 -L0.0.0.0:7722:localhost:22`"
								displayText=""
								:magicIcon="'magic'"
								v-if="type === AuditTrailsPageType.Active"
							/>

							<span v-if="type !== AuditTrailsPageType.Active"
								>{{ slotProps.data.PublicIP }}
							</span>
						</div>
					</template>
				</Column>

				<Column
					field="StartTime"
					header="Local Start Date/Time"
					sortable
				>
					<template #body="slotProps">
						{{ formatDateTime(new Date(slotProps.data.StartTime)) }}
					</template>
				</Column>

				<Column
					field="EndTime"
					header="Duration"
					sortable
					v-if="props.type === AuditTrailsPageType.History"
				>
					<template #body="slotProps">
						{{ getDuration(slotProps.data.StartTime, slotProps.data.EndTime) }}
					</template>
				</Column>

				<Column
					field="RemoteId"
					header="View Details"
				>
					<template #body="slotProps">
						<button
							@click="storeTask(slotProps.data)"
							class="p-link"
							style="width: fit-content"
						>
							<span class="material-icons">visibility</span>
						</button>
					</template>
				</Column>

				<Column
					field="EndTime"
					header="Terminate Connection"
				>
					<template #body="slotProps">
						<div v-if="slotProps.data.EndTime == 'N/A'">
							<button
								@click="emit('terminateConnection', slotProps.data.RemoteId)"
								class="p-link"
								style="width: fit-content"
							>
								<img
									src="@/assets/activeConnections/cancel_icon.svg"
									alt="user icon"
									class="h-20 w-10"
								/>
							</button>
						</div>
					</template>
				</Column>

				<!-- Empty and Loading Templates -->
				<template #empty>
					<span
						v-if="errorMessage"
						class="text-error"
					>
						{{ props.errorMessage }}</span
					>
					<span v-else>No connections found</span>
				</template>
				<template #loading
					><Loading :typeOfData="`${props.type} Connections`" />
				</template>
			</DataTable>
		</div>
	</div>
</template>
<style scoped>
	.custom-button {
		display: flex;
		align-items: center;
		justify-content: center;
		border: none;
		border-radius: 4px; /* Rounded corners */
		padding: 10px 15px; /* Padding around the text */
		cursor: pointer; /* Cursor changes to pointer on hover */
		transition:
			background-color 0.3s,
			transform 0.2s; /* Transition for smooth effects */
	}

	.custom-button:hover {
		transform: scale(0.97); /* Slightly shrink the button when clicked */
	}

	.custom-button:active {
		transform: scale(0.95); /* Slightly shrink the button when clicked */
	}
</style>
